import { ACTION, IconName, useDisclosure } from '@talos/kyoko';
import type { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';
import { PositionTypeEnum, useRoleAuth } from '../../../hooks';
import { type UpdatePositionPrimer, UpdatePositionsDialog } from '../PositionsV3/UpdatePositionsDialog';
import { useUpdateBalancesAccounts } from './useUpdateBalancesAccounts';

interface UseLegacyUpdateBalancesDialogParams<T> {
  enabled: boolean;
  getMarketAccount: (item: T) => string | undefined;
  getAsset: (item: T) => string | undefined;
  /**
   * If you want the user to be able to right-click the market account grouping row in the blotter and be able to open the dialog
   * for that market account, then provide the field the blotter is using to get the market account off the entity.
   */
  blotterMarketAccountField?: string;
}

/**
 * Legacy "Update Balances" flow: UpdatePositionsDialog + PUT /positions/market-accounts.
 * Separate disclosure/state from Adjust Market Account Position (asset-transactions).
 * Gated by org EnableLegacyUpdateBalances — remove when server 2.54 is retired.
 */
export const useLegacyUpdateBalancesDialog = <T,>({
  enabled,
  getMarketAccount,
  getAsset,
  blotterMarketAccountField,
}: UseLegacyUpdateBalancesDialogParams<T>) => {
  const [primer, setPrimer] = useState<UpdatePositionPrimer | undefined>(undefined);
  const { isAuthorized } = useRoleAuth();
  const updateBalancesDialog = useDisclosure({ onClose: () => setPrimer(undefined) });
  const { open } = updateBalancesDialog;
  const { accounts: updatableAccounts, accountsSet: updatableAccountsSet } = useUpdateBalancesAccounts();
  const hasAllowlist = (updatableAccounts?.length ?? 0) > 0;
  const show = enabled && hasAllowlist && isAuthorized(ACTION.EDIT_POSITION);

  const legacyUpdateBalancesDialogComponent = useMemo(() => {
    if (!show) {
      return null;
    }

    return (
      <UpdatePositionsDialog
        dialogProps={updateBalancesDialog}
        customMarketAccountOptions={updatableAccounts}
        positionType={PositionTypeEnum.MarketAccountPosition}
        positionPrimer={primer}
        useBalancesTerminology
      />
    );
  }, [show, updateBalancesDialog, updatableAccounts, primer]);

  const getLegacyUpdateBalanceContextMenuItem = useCallback(
    (params: GetContextMenuItemsParams<T>): MenuItemDef | undefined => {
      if (!show) {
        return undefined;
      }

      const data = params.node?.data;
      let mktAcc: string | undefined = undefined;
      let asset: string | undefined = undefined;

      if (data) {
        mktAcc = getMarketAccount(data);
        asset = getAsset(data);
      } else if (blotterMarketAccountField) {
        const isMarketAccountGroupNode = params.node?.field === blotterMarketAccountField;
        if (isMarketAccountGroupNode) {
          mktAcc = params.node?.key ?? undefined;
        }
      }

      if (!mktAcc || !updatableAccountsSet?.has(mktAcc)) {
        return undefined;
      }

      return {
        name: asset ? 'Update Balance' : 'Update Balances',
        icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
        action: () => {
          setPrimer({ MarketAccount: mktAcc, Asset: asset });
          open();
        },
      };
    },
    [show, blotterMarketAccountField, updatableAccountsSet, getMarketAccount, getAsset, open]
  );

  const openLegacyUpdateBalancesDialog = useCallback(() => {
    if (!show) {
      return;
    }
    setPrimer(undefined);
    open();
  }, [show, open]);

  return {
    legacyUpdateBalancesDialogComponent,
    getLegacyUpdateBalanceContextMenuItem,
    openLegacyUpdateBalancesDialog,
    showLegacyUpdateBalancesButton: show,
  };
};
